import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PERFIS, ROTULO_PERFIL, type Perfil } from '@arena/shared/permissoes';
import type { ArenaDaPlataforma, NovoMembroDaPlataforma } from '@arena/shared/schemas';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { credencialDeOperador, plataformaApi } from '../api/operacao.js';
import { Erro, EsqueletoDeTabela } from '../ui/estados.jsx';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Card, Formulario, Nota, Status, Tabela, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { CabecalhoDoOperador } from './CabecalhoDoOperador.jsx';
import { EntradaDoOperador } from './PaginaDeOperacao.jsx';

/**
 * O painel da plataforma: as arenas do sistema, criar arena com o dono
 * e vincular pessoas a uma arena.
 *
 * Como a tela de operação, fica FORA do shell e da sessão de arena: usa
 * a credencial `OPERACAO_TOKEN`, guardada na mesma chave da aba. Entrar
 * numa das duas telas vale para a outra.
 */

type Saida = 'saiu' | 'recusada';

export function PaginaDaPlataforma() {
  const cliente = useQueryClient();
  const [token, setToken] = useState<string | null>(() => credencialDeOperador.ler());
  const [recusada, setRecusada] = useState(false);

  if (!token) {
    return (
      <EntradaDoOperador
        titulo="Plataforma"
        descricao="Arenas, donos e equipes — o cadastro de quem usa o sistema."
        recusada={recusada}
        onEntrar={(novo) => {
          credencialDeOperador.gravar(novo);
          setRecusada(false);
          setToken(novo);
        }}
      />
    );
  }

  return (
    <PainelDaPlataforma
      token={token}
      onSair={(motivo: Saida) => {
        credencialDeOperador.limpar();
        cliente.removeQueries({ queryKey: ['plataforma'] });
        setRecusada(motivo === 'recusada');
        setToken(null);
      }}
    />
  );
}

// ---------------------------------------------------------------------

function PainelDaPlataforma({ token, onSair }: { token: string; onSair: (motivo: Saida) => void }) {
  const cliente = useQueryClient();
  const avisar = useToast();
  const [novaArenaAberta, setNovaArenaAberta] = useState(false);
  const [arenaDoMembro, setArenaDoMembro] = useState<ArenaDaPlataforma | null>(null);

  const arenas = useQuery({
    queryKey: ['plataforma', 'arenas'],
    queryFn: () => plataformaApi.arenas(token),
    retry: false,
    staleTime: 0,
  });

  const recusada = arenas.error instanceof ErroDeApi && arenas.error.status === 401;
  useEffect(() => {
    if (recusada) onSair('recusada');
  }, [recusada, onSair]);

  const recarregar = () => void cliente.invalidateQueries({ queryKey: ['plataforma', 'arenas'] });

  return (
    <>
      <CabecalhoDoOperador titulo="Plataforma" onSair={() => onSair('saiu')} />
      <div className="main">
        <div className="page-head">
          <div>
            <h1>Arenas</h1>
            <p>Cada arena tem a sua equipe, os seus dados e o seu endereço de reservas.</p>
          </div>
          <div className="head-actions">
            <Botao icone="plus" onClick={() => setNovaArenaAberta(true)}>
              Nova arena
            </Botao>
          </div>
        </div>

        <Nota>
          <b>Fora do aplicativo das arenas.</b> Cada arena nasce com os quatro perfis, a matriz de
          permissões, duas quadras e o plano de contas. Quem já tem cadastro é só vinculado — a
          senha dela não muda.
        </Nota>

        <Card titulo="Arenas" {...(arenas.data ? { dica: `${arenas.data.length} ativa(s)` } : {})}>
          {arenas.isPending ? (
            <EsqueletoDeTabela linhas={4} colunas={5} />
          ) : arenas.isError ? (
            <Erro
              tipo={arenas.error instanceof ErroDeRede ? 'rede' : 'falha'}
              detalhe={mensagemDoErro(arenas.error)}
              {...(arenas.error instanceof ErroDeApi && arenas.error.requestId
                ? { codigo: arenas.error.requestId }
                : {})}
              onTentarDeNovo={() => void arenas.refetch()}
            />
          ) : arenas.data.length === 0 ? (
            <Vazio
              acao={
                <Botao icone="plus" onClick={() => setNovaArenaAberta(true)}>
                  Criar a primeira arena
                </Botao>
              }
            >
              Nenhuma arena ainda.
            </Vazio>
          ) : (
            <Tabela colunas={['Arena', 'Endereço', 'Fuso', 'Equipe', '']}>
              {arenas.data.map((arena) => (
                <tr key={arena.id}>
                  <td>
                    <b>{arena.nome}</b>
                  </td>
                  <td className="num">{arena.slug}</td>
                  <td className="num">{arena.fuso}</td>
                  <td style={{ whiteSpace: 'normal', minWidth: 280 }}>
                    {arena.membros.length === 0
                      ? '—'
                      : arena.membros.map((m) => (
                          <div key={m.usuarioId}>
                            {m.nome} · <span className="num">{m.email}</span> ·{' '}
                            {ROTULO_PERFIL[m.perfil as Perfil] ?? m.perfil}
                            {!m.ativa && (
                              <>
                                {' '}
                                <Status situacao="neutra">inativo</Status>
                              </>
                            )}
                          </div>
                        ))}
                  </td>
                  <td>
                    <Botao pequeno variante="contorno" onClick={() => setArenaDoMembro(arena)}>
                      Adicionar pessoa
                    </Botao>
                  </td>
                </tr>
              ))}
            </Tabela>
          )}
        </Card>

        {novaArenaAberta && (
          <ModalDeNovaArena
            token={token}
            onFechar={() => setNovaArenaAberta(false)}
            onCriada={() => {
              setNovaArenaAberta(false);
              recarregar();
              avisar('Arena criada. O dono já pode entrar.');
            }}
          />
        )}

        {arenaDoMembro && (
          <ModalDeNovoMembro
            token={token}
            arena={arenaDoMembro}
            onFechar={() => setArenaDoMembro(null)}
            onVinculado={(novo) => {
              setArenaDoMembro(null);
              recarregar();
              avisar(
                novo ? 'Pessoa cadastrada e vinculada.' : 'Pessoa já cadastrada: só vinculada.',
              );
            }}
          />
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------

/** Minúsculas, dígitos e hífen — o formato de `zSlugPublico`. */
function slugDe(nome: string): string {
  return nome
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63);
}

function CaixaDeErro({ erro }: { erro: unknown }) {
  if (!erro) return null;
  const campos = erro instanceof ErroDeApi ? erro.campos : undefined;
  return (
    <div className="empty" role="alert" style={{ borderColor: 'var(--red)', marginBottom: 14 }}>
      {mensagemDoErro(erro)}
      {campos &&
        Object.entries(campos).map(([campo, mensagens]) => (
          <div key={campo} style={{ fontSize: 12, marginTop: 4 }}>
            {campo}: {mensagens.join(' ')}
          </div>
        ))}
    </div>
  );
}

function ModalDeNovaArena({
  token,
  onFechar,
  onCriada,
}: {
  token: string;
  onFechar: () => void;
  onCriada: () => void;
}) {
  const [nome, setNome] = useState('');
  const [slug, setSlug] = useState('');
  const [slugEditado, setSlugEditado] = useState(false);
  const [fuso, setFuso] = useState('America/Sao_Paulo');
  const [donoNome, setDonoNome] = useState('');
  const [donoEmail, setDonoEmail] = useState('');
  const [donoSenha, setDonoSenha] = useState('');

  const criar = useMutation({
    mutationFn: () =>
      plataformaApi.criarArena(token, {
        nome: nome.trim(),
        slug,
        fuso: fuso.trim(),
        dono: {
          nome: donoNome.trim(),
          email: donoEmail.trim(),
          ...(donoSenha ? { senha: donoSenha } : {}),
        },
      }),
    onSuccess: onCriada,
  });

  const completo = nome.trim().length >= 2 && slug && donoNome.trim().length >= 2 && donoEmail;

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="media"
      titulo="Nova arena"
      descricao="A arena nasce com perfis, matriz, quadras e plano de contas — e com o dono."
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao disabled={!completo || criar.isPending} onClick={() => criar.mutate()}>
            Criar arena
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <CaixaDeErro erro={criar.error} />
        <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Nome da arena">
            <input
              value={nome}
              autoFocus
              placeholder="Ex.: Arena Beira-Mar"
              onChange={(e) => {
                setNome(e.target.value);
                if (!slugEditado) setSlug(slugDe(e.target.value));
              }}
            />
          </Campo>
          <Campo rotulo="Endereço (slug)" ajuda="Usado no formulário público de reservas.">
            <input
              value={slug}
              onChange={(e) => {
                setSlugEditado(true);
                setSlug(slugDe(e.target.value));
              }}
            />
          </Campo>
          <Campo rotulo="Fuso horário">
            <input value={fuso} onChange={(e) => setFuso(e.target.value)} />
          </Campo>
          <div />
          <Campo rotulo="Nome do dono">
            <input value={donoNome} onChange={(e) => setDonoNome(e.target.value)} />
          </Campo>
          <Campo rotulo="E-mail do dono">
            <input
              type="email"
              autoComplete="off"
              value={donoEmail}
              onChange={(e) => setDonoEmail(e.target.value)}
            />
          </Campo>
          <Campo
            rotulo="Senha inicial"
            ajuda="Mínimo de 10 caracteres. Em branco se o e-mail já tiver cadastro."
          >
            <input
              type="password"
              autoComplete="new-password"
              value={donoSenha}
              onChange={(e) => setDonoSenha(e.target.value)}
            />
          </Campo>
        </Formulario>
      </CorpoDeModal>
    </Modal>
  );
}

function ModalDeNovoMembro({
  token,
  arena,
  onFechar,
  onVinculado,
}: {
  token: string;
  arena: ArenaDaPlataforma;
  onFechar: () => void;
  onVinculado: (usuarioNovo: boolean) => void;
}) {
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [perfil, setPerfil] = useState<NovoMembroDaPlataforma['perfil']>('gestao');

  const vincular = useMutation({
    mutationFn: () =>
      plataformaApi.adicionarMembro(token, arena.id, {
        nome: nome.trim(),
        email: email.trim(),
        perfil,
        ...(senha ? { senha } : {}),
      }),
    onSuccess: (vinculo) => onVinculado(vinculo.usuarioNovo),
  });

  return (
    <Modal
      aberto
      onFechar={onFechar}
      largura="estreita"
      titulo="Adicionar pessoa"
      descricao={arena.nome}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Cancelar
          </Botao>
          <Botao
            disabled={nome.trim().length < 2 || !email || vincular.isPending}
            onClick={() => vincular.mutate()}
          >
            Adicionar
          </Botao>
        </>
      }
    >
      <CorpoDeModal>
        <CaixaDeErro erro={vincular.error} />
        <Formulario onSubmit={(e) => e.preventDefault()}>
          <Campo rotulo="Nome">
            <input value={nome} autoFocus onChange={(e) => setNome(e.target.value)} />
          </Campo>
          <Campo rotulo="E-mail">
            <input
              type="email"
              autoComplete="off"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Campo>
          <Campo rotulo="Perfil">
            <select
              value={perfil}
              onChange={(e) => setPerfil(e.target.value as NovoMembroDaPlataforma['perfil'])}
            >
              {PERFIS.map((p) => (
                <option key={p} value={p}>
                  {ROTULO_PERFIL[p]}
                </option>
              ))}
            </select>
          </Campo>
          <Campo
            rotulo="Senha inicial"
            ajuda="Mínimo de 10 caracteres. Em branco se o e-mail já tiver cadastro."
          >
            <input
              type="password"
              autoComplete="new-password"
              value={senha}
              onChange={(e) => setSenha(e.target.value)}
            />
          </Campo>
        </Formulario>
      </CorpoDeModal>
    </Modal>
  );
}
